import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../services/api';
import { 
  FileText, Check, X, Clock, AlertCircle, 
  ChevronRight, RefreshCw, User, Mail, Calendar,
  Shield, Eye, ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from './ui/dialog';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle
} from './ui/sheet';

const STATUS_CONFIG = {
  pending: { color: 'bg-gold-600/20 text-gold-400', label: 'Pending' },
  submitted: { color: 'bg-gold-600/20 text-gold-400', label: 'Submitted' },
  under_review: { color: 'bg-blue-600/20 text-blue-400', label: 'Under Review' },
  verified: { color: 'bg-green-600/20 text-green-400', label: 'Verified' },
  approved: { color: 'bg-green-600/20 text-green-400', label: 'Approved' },
  rejected: { color: 'bg-red-600/20 text-red-400', label: 'Rejected' }
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const KYCManagementPanel = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [rejectDialog, setRejectDialog] = useState({ open: false, user: null });
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [sortBy, setSortBy] = useState('submitted_desc'); // submitted_desc, submitted_asc, name

  const fetchPendingKYC = useCallback(async () => {
    try {
      const response = await api.get('/admin/kyc/pending');
      setPendingSubmissions(response.data.submissions || []);
    } catch (error) {
      console.error('Failed to fetch pending KYC:', error);
      toast.error('Failed to load KYC submissions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchEmailLogs = async (userId) => {
    try {
      const response = await api.get(`/admin/email-logs?user_id=${userId}`);
      setEmailLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch email logs:', error);
      setEmailLogs([]);
    }
  };

  useEffect(() => {
    fetchPendingKYC();
  }, [fetchPendingKYC]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPendingKYC();
  };

  const handleViewUser = async (user) => {
    setSelectedUser(user);
    await fetchEmailLogs(user.id);
  };

  const handleApprove = async (userId) => {
    setProcessing(true);
    try {
      await api.post(`/admin/kyc/${userId}/approve`);
      toast.success('KYC approved successfully. Notification email sent.');
      setSelectedUser(null);
      fetchPendingKYC();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve KYC');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.user || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setProcessing(true);
    try {
      await api.post(`/admin/kyc/${rejectDialog.user.id}/reject?reason=${encodeURIComponent(rejectionReason)}`);
      toast.success('KYC rejected. Notification email sent.');
      setRejectDialog({ open: false, user: null });
      setRejectionReason('');
      setSelectedUser(null);
      fetchPendingKYC();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject KYC');
    } finally {
      setProcessing(false);
    }
  };

  // Sort submissions
  const sortedSubmissions = [...pendingSubmissions].sort((a, b) => {
    const dateA = new Date(a.kyc_documents?.submitted_at || a.updated_at || 0);
    const dateB = new Date(b.kyc_documents?.submitted_at || b.updated_at || 0);
    
    switch (sortBy) {
      case 'submitted_asc':
        return dateA - dateB;
      case 'name':
        return (a.full_name || '').localeCompare(b.full_name || '');
      case 'submitted_desc':
      default:
        return dateB - dateA;
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gold-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with stats and actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-gold-400" />
            <span className="font-medium">
              {pendingSubmissions.length} Pending Submission{pendingSubmissions.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-white/5 border border-white/10 rounded px-3 py-1.5 text-sm"
          >
            <option value="submitted_desc">Newest First</option>
            <option value="submitted_asc">Oldest First</option>
            <option value="name">By Name</option>
          </select>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Empty State */}
      {pendingSubmissions.length === 0 && (
        <div className="text-center py-12">
          <Check className="w-12 h-12 mx-auto text-green-400/50 mb-4" />
          <h3 className="text-lg font-medium text-green-400">All Caught Up!</h3>
          <p className="text-sm text-muted-foreground mt-2">
            No pending KYC submissions to review.
          </p>
        </div>
      )}

      {/* Pending Queue */}
      <div className="space-y-3">
        {sortedSubmissions.map((user) => {
          const kycDocs = user.kyc_documents || {};
          const submittedAt = kycDocs.submitted_at || user.updated_at;
          const config = STATUS_CONFIG[user.kyc_status] || STATUS_CONFIG.pending;
          
          return (
            <Card 
              key={user.id} 
              className="border-white/10 hover:border-gold-600/30 transition-all cursor-pointer"
              onClick={() => handleViewUser(user)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gold-600/20 flex items-center justify-center">
                      <User className="w-6 h-6 text-gold-400" />
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{user.full_name || 'Unknown User'}</h4>
                        <Badge className={config.color}>{config.label}</Badge>
                        <Badge variant="outline" className="text-xs">{user.role}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        <Clock className="w-3 h-3 inline mr-1" />
                        Submitted: {formatDate(submittedAt)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="btn-gold-metallic"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApprove(user.id);
                      }}
                      disabled={processing}
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-600/50 text-red-400 hover:bg-red-950/30"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRejectDialog({ open: true, user });
                      }}
                    >
                      <X className="w-3 h-3 mr-1" />
                      Reject
                    </Button>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* User Detail Sheet */}
      <Sheet open={!!selectedUser} onOpenChange={(open) => !open && setSelectedUser(null)}>
        <SheetContent className="bg-card border-border w-[500px] sm:max-w-[500px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-gold-400" />
              KYC Review
            </SheetTitle>
            <SheetDescription>
              {selectedUser?.full_name || 'Unknown User'}
            </SheetDescription>
          </SheetHeader>

          {selectedUser && (
            <div className="mt-6 space-y-6">
              {/* User Info */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gold-400">User Information</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Name</p>
                    <p className="font-medium">{selectedUser.full_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Role</p>
                    <p className="font-medium capitalize">{selectedUser.role}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedUser.email}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">User ID</p>
                    <p className="font-medium font-mono text-xs">{selectedUser.id?.slice(0, 8).toUpperCase()}</p>
                  </div>
                </div>
              </div>

              {/* KYC Documents */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gold-400">Submitted Documents</h4>
                <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3">
                  {selectedUser.kyc_documents ? (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Document Type:</span>
                        <span className="text-sm font-medium capitalize">
                          {selectedUser.kyc_documents.document_type?.replace(/_/g, ' ') || 'Emirates ID'}
                        </span>
                      </div>
                      {selectedUser.kyc_documents.document_number && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Document Number:</span>
                          <span className="text-sm font-medium font-mono">
                            {selectedUser.kyc_documents.document_number}
                          </span>
                        </div>
                      )}
                      {selectedUser.kyc_documents.expiry_date && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Expiry Date:</span>
                          <span className="text-sm font-medium">
                            {selectedUser.kyc_documents.expiry_date}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Submitted At:</span>
                        <span className="text-sm font-medium">
                          {formatDate(selectedUser.kyc_documents.submitted_at)}
                        </span>
                      </div>
                      
                      {/* Document Links */}
                      {(selectedUser.kyc_documents.document_front_url || selectedUser.kyc_documents.document_back_url) && (
                        <div className="pt-3 border-t border-white/10 space-y-2">
                          {selectedUser.kyc_documents.document_front_url && (
                            <a 
                              href={selectedUser.kyc_documents.document_front_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-gold-400 hover:text-gold-300"
                            >
                              <Eye className="w-4 h-4" />
                              View Front Document
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                          {selectedUser.kyc_documents.document_back_url && (
                            <a 
                              href={selectedUser.kyc_documents.document_back_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-gold-400 hover:text-gold-300"
                            >
                              <Eye className="w-4 h-4" />
                              View Back Document
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">No document metadata available</p>
                  )}
                </div>
              </div>

              {/* Email Audit Trail */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gold-400">Email Audit Trail</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {emailLogs.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-3 bg-white/5 rounded">
                      No emails sent yet
                    </p>
                  ) : (
                    emailLogs.map((log, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg text-sm ${
                          log.status === 'sent' 
                            ? 'bg-green-950/20 border border-green-600/30' 
                            : 'bg-red-950/20 border border-red-600/30'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{log.subject}</span>
                          <Badge className={log.status === 'sent' ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}>
                            {log.status}
                          </Badge>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          <p>To: {log.recipient}</p>
                          <p>Time: {formatDate(log.timestamp)}</p>
                          {log.email_id && (
                            <p className="font-mono">ID: {log.email_id}</p>
                          )}
                          {log.error && (
                            <p className="text-red-400">Error: {log.error}</p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <Button
                  className="flex-1 btn-gold-metallic"
                  onClick={() => handleApprove(selectedUser.id)}
                  disabled={processing}
                >
                  {processing ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> Approve KYC</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-red-600/50 text-red-400 hover:bg-red-950/30"
                  onClick={() => setRejectDialog({ open: true, user: selectedUser })}
                  disabled={processing}
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, user: null })}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-red-400">Reject KYC Submission</DialogTitle>
            <DialogDescription>
              Reject KYC for {rejectDialog.user?.full_name || 'this user'}. 
              They will receive an email with the reason and can re-submit.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label htmlFor="rejection-reason">Rejection Reason *</Label>
            <textarea
              id="rejection-reason"
              placeholder="e.g., Document image is unclear, expiry date not visible, etc."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="mt-2 w-full h-24 px-3 py-2 rounded-md bg-white/5 border border-white/10 focus:border-red-600 focus:outline-none resize-none"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-1">
              This reason will be sent to the user in the rejection email.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleReject}
              disabled={processing || !rejectionReason.trim()}
            >
              {processing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Rejecting...</>
              ) : (
                'Reject & Send Email'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KYCManagementPanel;
